import { Dialog } from 'primereact/dialog'
import React, { useEffect, useMemo, useState } from 'react'
import classes from './CertificateDetailsAttachmentsRPDialog.module.scss'
import CloseIcon from '../../../../../shared/close-icon/CloseIcon'
import AddAttachmentsRP from '../../add-new-certificate/rp-library/add-attachments-rp/AddAttachmentsRP'
import { IAddAttachmentsRPTestReportModel } from '../../add-new-certificate/rp-library/shared/interfaces/IAddAttachmentsRPTestReportModel'
import { IAddAttachmentEditFormData } from '../../add-new-certificate/rp-library/shared/interfaces/non-model/IAddAttachmentEditFormData'
import useRPTestReportValidator from '../../add-new-certificate/rp-library/shared/hooks/useRPTestReportValidator'
import { ConfirmDialog, ConfirmDialogProps } from 'primereact/confirmdialog'
import CertificateListService from '../../../../../services/CertificateListService'
import { objectToFormData } from '../../../../../utils/helpers/object.helpers'
import { chunkArray } from '../../../../../utils/helpers/array.helpers'
import useToastr from '../../../../../hooks/useToastr'
import { useDispatch } from 'react-redux'
import { reloadCertificateDetails } from '../../../../../features/certificateList/certificateListSlice'
import { AddAttachmentsRPUtils } from '../../add-new-certificate/rp-library/shared/utils/AddAttachmentsRPUtils'
import { displayLinkEcmModalHeader } from '../../add-new-certificate/rp-library/shared/constants/AddAttachmentsRPConstants'
import { CertificateDocumentStatus } from '../../../../../enums/CertificateDocumentStatus'
import { SchemeEcmType } from '../../../../../enums/SchemeEcmType'

const attachmentUploadBatchSize = 20

interface ICertificateDetailsAttachmentsRPDialogProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  details: any
  rpAttachments: IAddAttachmentsRPTestReportModel[]
  setRPAttachments: React.Dispatch<React.SetStateAction<IAddAttachmentsRPTestReportModel[]>>
  projectDetailsValues?: any
  certificateDetailsFromE2E?: any
  fromEditData?: IAddAttachmentEditFormData
}

const initialConfirmDialogValues: ConfirmDialogProps = {
  visible: false,
  header: '',
  footer: '',
  message: '',
  className: 'custom-confirm-dialog',
  style: { width: '520px' },
  onHide: () => {},
}

const CertificateDetailsAttachmentsRPDialog = (
  props: ICertificateDetailsAttachmentsRPDialogProps
) => {
  const { showError, showInfo, showSuccess } = useToastr()
  const dispatch = useDispatch()

  const [isSavingInProgress, setIsSavingInProgress] = useState<boolean>(false)
  const isRPValid = useRPTestReportValidator(props.rpAttachments)
  const [confirmDialogProps, setConfirmDialogProps] = useState<ConfirmDialogProps>(
    initialConfirmDialogValues
  )
  const [hasUpdatedAttachments, setHasUpdatedAttachments] = useState<boolean>(false)

  const initialRPAttachments = useMemo(() => {
    if (props.isOpen || hasUpdatedAttachments) {
      setHasUpdatedAttachments(false)
      const applyInitialRPAttachments = props.rpAttachments
        // if there is a recent synced document/s re-initialize initial RP without newly uploaded files;

        // check for newly added parent documents
        ?.filter(rpAttachment => !rpAttachment.file)
        // remove unsaved supporting documents and keep only saved ones
        ?.map(rpAttachment => ({
          ...rpAttachment,
          supportingDocuments:
            rpAttachment.supportingDocuments?.filter(suppDoc => !suppDoc.file) || [],
        }))

      return applyInitialRPAttachments
    } else return []

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, hasUpdatedAttachments])

  // reset attachments to remove unsaved files when modal reopens
  useEffect(() => {
    if (props.isOpen && initialRPAttachments?.length >= 0) {
      props.setRPAttachments(initialRPAttachments)
    }
  }, [props.isOpen])

  const hasModifiedOriginalValues =
    JSON.stringify(AddAttachmentsRPUtils.removeExcludedProperties(initialRPAttachments)) !==
    JSON.stringify(AddAttachmentsRPUtils.removeExcludedProperties(props.rpAttachments))

  const hasLinkToECM = initialRPAttachments?.some((rp: IAddAttachmentsRPTestReportModel) => {
    return rp.status === CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic
  })

  const isDisableSaving = !isRPValid

  const closeConfirmDialog = () => {
    setConfirmDialogProps(initialConfirmDialogValues)
  }

  const onCancel = (isCloseOnly: boolean) => {
    if (isCloseOnly) {
      props.setIsOpen(false)
      return
    }

    if (hasModifiedOriginalValues) {
      setConfirmDialogProps(current => ({
        ...current,
        header: 'Cancel Upload Confirmation',
        visible: true,
        message: confirmCancelDialogBody(),
        footer: () => customCancelFooter(),
        onHide: closeConfirmDialog,
      }))
    } else {
      resetRPAttachmentsOnCloseModal()
    }
  }

  const resetRPAttachmentsOnCloseModal = (closeModal: boolean = false) => {
    if (closeModal) setConfirmDialogProps(initialConfirmDialogValues)

    props.setIsOpen(false)
  }

  const saveProcess = async (isLinkAndEcm: boolean) => {
    setIsSavingInProgress(true)

    const rpAttachmentsFormData = objectToFormData(
      AddAttachmentsRPUtils.convertRPModelToAttachmentFileRequestModel(
        props.details?.certificateId,
        props.rpAttachments,
        props.details,
        isLinkAndEcm
      )
    )

    let response =
      await CertificateListService.certificateAttachmentRPSaveLinkUploadECM(rpAttachmentsFormData)

    if (response?.isSuccess) {
      if (isLinkAndEcm) {
        showInfo(
          'Document Upload Ongoing',
          'The document(s) you added is now being uploaded to Workbench and ECM TIC. You will receive a notification once completed.'
        )
      } else {
        showSuccess(
          'Success!',
          `The document(s) has been uploaded temporarily to GMAWB File Storage.`
        )
      }

      dispatch(reloadCertificateDetails(true))
      setConfirmDialogProps(initialConfirmDialogValues)
      resetRPAttachmentsOnCloseModal()
    } else {
      showError(
        'Error!',
        response ? response.message : `Error while saving the certificate attachment`
      )
    }
    setIsSavingInProgress(false)
  }

  const confirmCancelDialogBody = () => {
    return (
      <>
        <div className="custom-confirm-dialog-body">
          <div className="custom-space-below">All files you uploaded will be discarded.</div>
          <div>Would you like to Continue?</div>
        </div>
      </>
    )
  }
  const customCancelFooter = () => {
    return (
      <div className="custom-confirm-dialog-footer">
        <button
          className="ul-button -app-primary -medium custom-confirm-dialog-accept-btn"
          onClick={() => resetRPAttachmentsOnCloseModal(true)}
        >
          YES
        </button>
        <button
          className="ul-button -medium default-cancel-button custom-confirm-dialog-reject-btn"
          onClick={closeConfirmDialog}
        >
          NO
        </button>
      </div>
    )
  }

  const confirmActivateFooter = () => {
    return (
      <>
        <div className="custom-confirm-dialog-footer">
          <button
            className="ul-button -app-primary -medium custom-confirm-dialog-accept-btn"
            onClick={() => {
              setConfirmDialogProps(initialConfirmDialogValues)
              saveProcess(true)
            }}
          >
            YES
          </button>
          <button
            className="ul-button -medium default-cancel-button custom-confirm-dialog-reject-btn"
            onClick={() => setConfirmDialogProps(initialConfirmDialogValues)}
          >
            NO
          </button>
        </div>
      </>
    )
  }

  const confirmActivateDialogBody = () => {
    return (
      <>
        <div className="custom-confirm-dialog-body">
          <div className="custom-space-below">
            File(s) will be saved to ECM TIC and cannot be deleted.
          </div>
          <div>Would you like to Continue?</div>
        </div>
      </>
    )
  }

  return (
    <>
      <Dialog
        style={{ width: '70vw', height: '75vh', margin: '0px' }}
        header="Add Attachments"
        visible={props.isOpen}
        footer={
          <>
            <hr className="modal-footer-hr" />
            <div className={`${classes['dialog-footer']} ${classes['right-items']}`}>
              <button
                className={`ul-button -small ${classes['dialog-cancel-btn']}`}
                onClick={() => onCancel(!hasModifiedOriginalValues)}
                disabled={isSavingInProgress}
              >
                CANCEL
              </button>
              <button
                className={`ul-button -small ${classes['dialog-save-btn']}`}
                disabled={isDisableSaving || isSavingInProgress || !hasModifiedOriginalValues}
                onClick={() => saveProcess(false)}
              >
                SAVE
              </button>
              {props.details?.certificateStatus === 'Active' && (
                <button
                  className={`ul-button -small ${classes['dialog-link-btn']}`}
                  disabled={
                    isDisableSaving ||
                    isSavingInProgress ||
                    !(hasLinkToECM || hasModifiedOriginalValues)
                  }
                  onClick={() =>
                    setConfirmDialogProps(current => ({
                      ...current,
                      visible: true,
                      header: displayLinkEcmModalHeader,
                      message: confirmActivateDialogBody,
                      footer: confirmActivateFooter,
                      onHide: closeConfirmDialog,
                    }))
                  }
                >
                  LINK FILE TO WB AND ECM TIC
                </button>
              )}
            </div>
          </>
        }
        contentClassName={classes['add-attachment-dialog-content']}
        onHide={() => {
          onCancel(false)
        }}
        icons={
          <CloseIcon
            onCloseEvent={() => {
              onCancel(false)
            }}
          />
        }
        closable={false}
      >
        <hr className="modal-header-hr" />

        <AddAttachmentsRP
          isDisplayNone={isSavingInProgress}
          isCertificateFromRevision={true}
          rpAttachments={props.rpAttachments}
          setRPAttachments={props.setRPAttachments}
          projectDetailsValues={props.projectDetailsValues}
          fromEditData={props.fromEditData}
          removeTitle={true}
          setHasUpdatedAttachments={setHasUpdatedAttachments}
          isEditOrModify={true}
          certificateId={props.details?.certificateId}
          uniqueCertificateId={props.details?.uniqueCertificateId}
        />

        {isSavingInProgress && (
          <div className={classes['loading-div']}>
            <div>Saving attachments......</div>
            <div>
              <svg className="ul-progressCircle -medium">
                <circle className="ul-progressCircle__outer" cx="50%" cy="50%" r="35%" />
                <circle className="ul-progressCircle__inner" cx="50%" cy="50%" r="35%" />
              </svg>
            </div>
          </div>
        )}
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  )
}

export default CertificateDetailsAttachmentsRPDialog
